import { Controller } from "@hotwired/stimulus"

// Duree d'affichage du feedback d'erreur (ms)
const ERROR_FEEDBACK_MS = 2000

// Liste des quiz d'une session live/presentiel, cote FORMATEUR : insertion
// des quiz lances, resultats par etudiant en temps reel, cloture/suppression.
// Recoit les evenements du canal via le controller de session (cf. data-action
// des vues show). Issu de la decomposition de live_session_quiz_controller
// (787 l., Lot 7.6) — le formulaire de creation vit dans quiz-composer, la
// vue etudiant dans student-quiz-board.
export default class extends Controller {
  static targets = ["quizList", "quizCard", "emptyState"]
  static values = {
    quizzesUrl: String,
    students: { type: Array, default: [] },
    labelActive: { type: String, default: "Actif" },
    labelClosed: { type: String, default: "Clôturé" },
    labelClose: { type: String, default: "Clôturer" },
    labelDeleteConfirm: { type: String, default: "Supprimer ce quiz ?" },
    labelAnswered: { type: String, default: "réponses" },
    labelNotAnswered: { type: String, default: "En attente" },
    labelMultiple: { type: String, default: "Réponses multiples" }
  }

  // --- Evenements dispatches par live-session / classroom-session controller ---

  onQuizLaunched(event) {
    const { quiz_id, question, options, allow_multiple } = event.detail
    if (!this.hasQuizListTarget) return

    const card = this.buildTrainerQuizCardElement(parseInt(quiz_id), question, options, allow_multiple)
    this.quizListTarget.prepend(card)
    if (this.hasEmptyStateTarget) this.emptyStateTarget.remove()
  }

  onQuizAnswered(event) {
    const { quiz_id, user_id, is_correct } = event.detail
    this.updateStudentResult(parseInt(quiz_id), parseInt(user_id), is_correct)
  }

  onQuizClosed(event) {
    const { quiz_id } = event.detail
    this.markQuizClosed(parseInt(quiz_id))
  }

  onQuizDeleted(event) {
    const { quiz_id } = event.detail
    const card = this.element.querySelector(
      `[data-quiz-id="${parseInt(quiz_id)}"][data-trainer-quiz-board-target="quizCard"]`
    )
    if (card) card.remove()
  }

  // --- Cloture d'un quiz via fetch ---

  async closeQuiz(event) {
    event.preventDefault()

    const button = event.currentTarget
    const quizId = parseInt(button.dataset.quizId)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(
        `${this.quizzesUrlValue}/${quizId}/close`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": token,
            "Accept": "application/json"
          }
        }
      )

      if (!response.ok) {
        this.flashError(button)
      }
    } catch (error) {
      console.error("[Quiz] Close error:", error)
      this.flashError(button)
    }
  }

  // --- Suppression d'un quiz via fetch ---

  async deleteQuiz(event) {
    event.preventDefault()

    if (!window.confirm(this.labelDeleteConfirmValue)) return

    const button = event.currentTarget
    const quizId = parseInt(button.dataset.quizId)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(
        `${this.quizzesUrlValue}/${quizId}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": token,
            "Accept": "application/json"
          }
        }
      )

      if (!response.ok) {
        this.flashError(button)
      }
    } catch (error) {
      console.error("[Quiz] Delete error:", error)
      this.flashError(button)
    }
  }

  // --- Mise a jour du resultat d'un etudiant ---

  updateStudentResult(quizId, userId, isCorrect) {
    const row = this.element.querySelector(
      `[data-student-row][data-quiz-id="${quizId}"][data-user-id="${userId}"]`
    )
    if (!row) return

    const resultEl = row.querySelector("[data-student-result]")
    if (resultEl) {
      resultEl.textContent = isCorrect ? "✓" : "✗"
      resultEl.className = isCorrect
        ? "text-xs font-bold text-emerald-600"
        : "text-xs font-bold text-red-500"
    }

    // Incrementer le compteur de reponses
    const countEl = this.element.querySelector(
      `[data-answer-count][data-quiz-id="${quizId}"]`
    )
    if (countEl) {
      const current = parseInt(countEl.textContent) || 0
      countEl.textContent = current + 1
    }
  }

  // --- Marquer un quiz comme cloture ---

  markQuizClosed(quizId) {
    const badge = this.element.querySelector(
      `[data-status-badge][data-quiz-id="${quizId}"]`
    )
    if (badge) {
      badge.className = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
      badge.textContent = this.labelClosedValue
    }

    // Retirer le bouton de fermeture
    const closeAction = this.element.querySelector(
      `[data-close-action][data-quiz-id="${quizId}"]`
    )
    if (closeAction) closeAction.remove()
  }

  // --- Construction DOM securisee de la carte formateur ---

  buildTrainerQuizCardElement(quizId, question, options, allowMultiple) {
    const card = document.createElement("div")
    card.className = "border border-gray-200 rounded-lg p-3"
    card.id = `session_quiz_${quizId}`
    card.dataset.trainerQuizBoardTarget = "quizCard"
    card.dataset.quizId = quizId

    // En-tete
    const header = document.createElement("div")
    header.className = "flex items-start justify-between mb-2"

    const titleWrap = document.createElement("div")
    const title = document.createElement("p")
    title.className = "text-sm font-medium text-gray-800"
    title.textContent = question
    titleWrap.appendChild(title)

    if (allowMultiple) {
      const multiLabel = document.createElement("p")
      multiLabel.className = "text-xs text-gray-400 mt-0.5"
      multiLabel.textContent = this.labelMultipleValue
      titleWrap.appendChild(multiLabel)
    }

    const badge = document.createElement("span")
    badge.className = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
    badge.dataset.statusBadge = ""
    badge.dataset.quizId = quizId
    badge.textContent = this.labelActiveValue

    header.appendChild(titleWrap)
    header.appendChild(badge)
    card.appendChild(header)

    // Options
    const optionsDiv = document.createElement("div")
    optionsDiv.className = "space-y-1 mb-2"
    options.forEach((option, index) => {
      const optRow = document.createElement("div")
      optRow.className = "flex items-center gap-2 text-xs text-gray-600"
      const numSpan = document.createElement("span")
      numSpan.className = "w-4 text-center font-medium"
      numSpan.textContent = `${index + 1}.`
      const textSpan = document.createElement("span")
      textSpan.textContent = option
      optRow.appendChild(numSpan)
      optRow.appendChild(textSpan)
      optionsDiv.appendChild(optRow)
    })
    card.appendChild(optionsDiv)

    // Tableau des etudiants
    const resultsSection = document.createElement("div")
    resultsSection.className = "border-t border-gray-100 pt-2"

    const countP = document.createElement("p")
    countP.className = "text-xs text-gray-500 mb-1"
    const countSpan = document.createElement("span")
    countSpan.dataset.answerCount = ""
    countSpan.dataset.quizId = quizId
    countSpan.textContent = "0"
    countP.appendChild(countSpan)
    countP.appendChild(document.createTextNode(` / ${this.studentsValue.length} ${this.labelAnsweredValue}`))
    resultsSection.appendChild(countP)

    const table = document.createElement("div")
    table.dataset.studentTable = ""
    table.dataset.quizId = quizId
    table.className = "space-y-0.5 max-h-32 overflow-y-auto"

    this.studentsValue.forEach(student => {
      const row = document.createElement("div")
      row.className = "flex items-center justify-between text-xs py-0.5"
      row.dataset.studentRow = ""
      row.dataset.quizId = quizId
      row.dataset.userId = student.id

      const nameSpan = document.createElement("span")
      nameSpan.className = "text-gray-600"
      nameSpan.textContent = student.name

      const resultSpan = document.createElement("span")
      resultSpan.dataset.studentResult = ""
      resultSpan.className = "text-gray-400"
      resultSpan.textContent = this.labelNotAnsweredValue

      row.appendChild(nameSpan)
      row.appendChild(resultSpan)
      table.appendChild(row)
    })

    resultsSection.appendChild(table)
    card.appendChild(resultsSection)

    // Actions
    const actions = document.createElement("div")
    actions.className = "mt-2 pt-2 border-t border-gray-100 flex items-center justify-between"

    const closeWrap = document.createElement("div")
    closeWrap.dataset.closeAction = ""
    closeWrap.dataset.quizId = quizId

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.className = "text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
    closeBtn.dataset.action = "click->trainer-quiz-board#closeQuiz"
    closeBtn.dataset.quizId = quizId

    const closeIcon = document.createElement("i")
    closeIcon.className = "fas fa-times-circle mr-1"
    closeBtn.appendChild(closeIcon)
    closeBtn.appendChild(document.createTextNode(this.labelCloseValue))
    closeWrap.appendChild(closeBtn)

    const deleteBtn = document.createElement("button")
    deleteBtn.type = "button"
    deleteBtn.className = "text-xs text-gray-400 hover:text-red-500 transition-colors"
    deleteBtn.dataset.action = "click->trainer-quiz-board#deleteQuiz"
    deleteBtn.dataset.quizId = quizId

    const deleteIcon = document.createElement("i")
    deleteIcon.className = "fas fa-trash-alt"
    deleteBtn.appendChild(deleteIcon)

    actions.appendChild(closeWrap)
    actions.appendChild(deleteBtn)
    card.appendChild(actions)

    return card
  }

  // --- Helper feedback ---

  flashError(element) {
    element.classList.add("ring-2", "ring-red-500")
    setTimeout(() => {
      element.classList.remove("ring-2", "ring-red-500")
    }, ERROR_FEEDBACK_MS)
  }
}
